import apiClient from '../client';
import type { ExpenseRequest, CreateExpenseRequest, PaginatedResponse } from '../../types';

const expenseService = {
  getAll: async (page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getMyExpenses: async (page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses/my?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getByProject: async (projectId: string, page = 0, size = 10) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses/project/${projectId}?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getByFarmOperation: async (farmOperationId: string, page = 0, size = 10) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses/operation/${farmOperationId}?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getByOperationTask: async (operationTaskId: string, page = 0, size = 10) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses/task/${operationTaskId}?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getByStatus: async (status: string, page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(
      `/expenses/status/${status}?page=${page}&size=${size}&sort=requestDate,desc`
    );
    return res.data;
  },

  getById: async (id: string) => {
    const res = await apiClient.get<ExpenseRequest>(`/expenses/${id}`);
    return res.data;
  },

  create: async (data: CreateExpenseRequest) => {
    const res = await apiClient.post<ExpenseRequest>('/expenses', data);
    return res.data;
  },

  update: async (id: string, data: Partial<CreateExpenseRequest>) => {
    const res = await apiClient.put<ExpenseRequest>(`/expenses/${id}`, data);
    return res.data;
  },

  approve: async (id: string) => {
    await apiClient.post(`/expenses/${id}/approve`);
  },

  reject: async (id: string, reason: string) => {
    await apiClient.post(`/expenses/${id}/reject`, null, { params: { reason } });
  },

  delete: async (id: string) => {
    await apiClient.delete(`/expenses/${id}`);
  },

  moveToProject: async (id: string, targetProjectId: string) => {
    const res = await apiClient.patch<ExpenseRequest>(`/expenses/${id}/move-to-project?targetProjectId=${targetProjectId}`);
    return res.data;
  },

  moveToOperation: async (id: string, targetFarmOperationId: string) => {
    const res = await apiClient.patch<ExpenseRequest>(`/expenses/${id}/move-to-operation?targetFarmOperationId=${targetFarmOperationId}`);
    return res.data;
  },

  search: async (params: { page?: number; size?: number; search?: string; status?: string; projectId?: string; farmOperationId?: string; operationTaskId?: string } = {}) => {
    const { page = 0, size = 10, search, status, projectId, farmOperationId, operationTaskId } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (status) qs.set('status', status);
    if (projectId) qs.set('projectId', projectId);
    if (farmOperationId) qs.set('farmOperationId', farmOperationId);
    if (operationTaskId) qs.set('operationTaskId', operationTaskId);
    const res = await apiClient.get<PaginatedResponse<ExpenseRequest>>(`/expenses/search?${qs}`);
    return res.data;
  },
};

export default expenseService;
